import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

export type ButtonVariant =
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";

export type ButtonSize =
    | "default"
    | "sm"
    | "lg"
    | "icon"
    | "icon-sm"
    | "icon-lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    className?: string;
    asChild?: boolean;
}

const baseClasses = [
    "tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap",
    "tw-rounded-md tw-text-sm tw-font-medium",
    "tw-transition-all",
    "tw-[&_svg]:tw-pointer-events-none tw-[&_svg:not([class*='size-'])]:tw-size-4 tw-[&_svg]:tw-shrink-0",
    "tw-shrink-0",
    "focus:tw-outline-none focus:tw-ring-[3px] focus:tw-ring-primary/50 focus:tw-border tw-border-transparent",
    "disabled:tw-pointer-events-none disabled:tw-opacity-50",
    "aria-[invalid=true]:tw-ring-red-500/20 aria-[invalid=true]:tw-border-red-500",
];

const sizeClasses: Record<ButtonSize, string> = {
    default: "tw-h-9 tw-px-4 tw-py-2 tw-has-[>svg]:tw-px-3",
    sm: "tw-h-8 tw-rounded-md tw-gap-1.5 tw-px-3 tw-has-[>svg]:tw-px-2.5",
    lg: "tw-h-10 tw-rounded-md tw-px-6 tw-has-[>svg]:tw-px-4",
    icon: "tw-size-9",
    "icon-sm": "tw-size-8",
    "icon-lg": "tw-size-10",
};

const variantClasses: Record<ButtonVariant, string> = {
    default: "tw-bg-primary tw-text-white hover:tw-bg-primary/90",
    destructive:
        "tw-bg-red-600 tw-text-white hover:tw-bg-red-600/90 focus:tw-ring-red-600/20",
    outline:
        "tw-border tw-bg-white tw-shadow-sm hover:tw-bg-gray-50 hover:tw-text-gray-900 dark:tw-bg-gray-800/30 dark:tw-border-gray-700 dark:hover:tw-bg-gray-800/50",
    secondary: "tw-bg-secondary tw-text-gray-900 hover:tw-bg-secondary/80",
    ghost: "hover:tw-bg-gray-100 hover:tw-text-gray-900 dark:hover:tw-bg-gray-800/50",
    link: "tw-text-primary tw-underline-offset-4 hover:tw-underline",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        { variant = "default", size = "default", className = "", asChild = false, children, ...props },
        ref,
    ) => {
        const Comp: any = asChild ? Slot : "button";

        const clx = [
            ...baseClasses,
            sizeClasses[size],
            variantClasses[variant],
            className,
        ]
            .filter(Boolean)
            .join(" ");

        return (
            <Comp ref={ref} className={clx} {...props}>
                {children}
            </Comp>
        );
    },
);

Button.displayName = "Button";

export default Button;
